/**
 * PlayerStateComponent — source-keyed state toggle tracker for the player.
 *
 * Manages boolean toggles (movement lock, knockback immunity, stagger immunity,
 * petrified, ghost form) and numeric values (damage reduction) that multiple skill sources
 * can set independently. Each source is identified by a string key (typically
 * a skillId). Aggregate queries resolve across all active sources:
 *   - Booleans: true if ANY source is true
 *   - Damage reduction: the MAXIMUM value across all sources
 *
 * This allows overlapping effects (e.g., two skills both granting knockback
 * immunity) without interfering with each other's cleanup.
 */

import type { Component } from './Component.js';

export class PlayerStateComponent implements Component {
  // --- Source-keyed boolean toggles ---
  private readonly _movementLocked = new Map<string, boolean>();
  private readonly _knockbackImmune = new Map<string, boolean>();
  private readonly _staggerImmune = new Map<string, boolean>();
  private readonly _petrified = new Map<string, boolean>();
  private readonly _ghostForm = new Map<string, boolean>();

  // --- Source-keyed numeric values ---
  private readonly _damageReduction = new Map<string, number>();

  // ── Boolean toggles ──────────────────────────────────────────────────

  setMovementLocked(sourceSkillId: string, locked: boolean): void {
    if (locked) {
      this._movementLocked.set(sourceSkillId, true);
    } else {
      this._movementLocked.delete(sourceSkillId);
    }
  }

  /** True if ANY source has locked movement. */
  get isMovementLocked(): boolean {
    return this._movementLocked.size > 0;
  }

  setKnockbackImmune(sourceSkillId: string, immune: boolean): void {
    if (immune) {
      this._knockbackImmune.set(sourceSkillId, true);
    } else {
      this._knockbackImmune.delete(sourceSkillId);
    }
  }

  /** True if ANY source grants knockback immunity. */
  get isKnockbackImmune(): boolean {
    return this._knockbackImmune.size > 0;
  }

  setStaggerImmune(sourceSkillId: string, immune: boolean): void {
    if (immune) {
      this._staggerImmune.set(sourceSkillId, true);
    } else {
      this._staggerImmune.delete(sourceSkillId);
    }
  }

  /** True if ANY source grants stagger immunity. */
  get isStaggerImmune(): boolean {
    return this._staggerImmune.size > 0;
  }

  setPetrified(sourceSkillId: string, active: boolean): void {
    if (active) {
      this._petrified.set(sourceSkillId, true);
    } else {
      this._petrified.delete(sourceSkillId);
    }
  }

  /** True if ANY source has set the petrified flag. */
  get isPetrified(): boolean {
    return this._petrified.size > 0;
  }

  setGhostForm(sourceSkillId: string, active: boolean): void {
    if (active) {
      this._ghostForm.set(sourceSkillId, true);
    } else {
      this._ghostForm.delete(sourceSkillId);
    }
  }

  /** True if ANY source has activated ghost form (intangible + invisible to idle AI). */
  get isGhostForm(): boolean {
    return this._ghostForm.size > 0;
  }

  // ── Numeric values ───────────────────────────────────────────────────

  setDamageReduction(sourceSkillId: string, value: number): void {
    if (value > 0) {
      this._damageReduction.set(sourceSkillId, value);
    } else {
      this._damageReduction.delete(sourceSkillId);
    }
  }

  /** Maximum damage reduction across all active sources (0 if none). */
  get damageReduction(): number {
    let max = 0;
    for (const v of this._damageReduction.values()) {
      if (v > max) max = v;
    }
    return max;
  }

  // ── Component interface ──────────────────────────────────────────────

  update(_delta: number): void {
    // State toggles are set/cleared imperatively — no per-frame work needed.
  }

  destroy(): void {
    this._movementLocked.clear();
    this._knockbackImmune.clear();
    this._staggerImmune.clear();
    this._petrified.clear();
    this._ghostForm.clear();
    this._damageReduction.clear();
  }
}
